import { useState, useEffect, useRef } from 'react';
import expertKredytowyLogo from '@/assets/expert-kredytowy-logo-960.webp';
import expertKredytowyLogoStandard from '@/assets/expert-kredytowy-logo-640.webp';
import commitmentBadge from '@/assets/zaangazowanie-100.webp';
import commitmentBadgeStandard from '@/assets/zaangazowanie-100-360.webp';

export default function HeroSection() {
  const [scrollY, setScrollY] = useState(0);
  const [videoReady, setVideoReady] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReducedMotion(media.matches);
    const onChange = (e: MediaQueryListEvent) => setReducedMotion(e.matches);
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const parallaxOffset = scrollY * 0.25;

  return (
    <section className="relative w-full overflow-hidden bg-background-50">

      {/* ============================================================
          DESKTOP – Split-screen: tekst po lewej, zdjęcie po prawej
      ============================================================ */}
      <div className="hidden lg:flex w-full min-h-[600px] lg:min-h-[680px] xl:min-h-[780px]">

        {/* Lewa kolumna – treść na jasnym tle biura */}
        <div className="w-[48%] xl:w-[46%] flex flex-col justify-center px-8 md:px-12 xl:px-20 py-14 md:py-16 relative">
          <div className="max-w-xl">
            {/* Logo marki */}
            <div className="flex items-center mb-10 animate-fade-in-up">
              <img
                src={expertKredytowyLogo}
                srcSet={`${expertKredytowyLogoStandard} 640w, ${expertKredytowyLogo} 960w`}
                sizes="(min-width: 1280px) 340px, 300px"
                alt="EXPERT KREDYTOWY — nowoczesne finansowanie"
                decoding="async"
                className="w-[300px] xl:w-[340px] h-auto object-contain object-left rounded-md transition-transform duration-500 hover:scale-[1.04]"
              />
            </div>

            {/* Nagłówek H1 */}
            <h1 className="font-heading text-[2.2rem] md:text-[2.6rem] xl:text-[3.4rem] font-bold leading-[1.12] text-foreground-950 mb-5 animate-fade-in-up animation-delay-100">
              Jan Wojdyła — ekspert kredytowy Sanok i Podkarpacie
            </h1>

            {/* Podtytuł */}
            <p className="text-sm font-medium tracking-[0.25em] uppercase text-primary-500 mb-6 animate-fade-in-up animation-delay-200">
              Ekspercka analiza finansowania 360°
            </p>

            {/* Elegancki separator */}
            <div className="flex items-center mb-8 animate-fade-in-up animation-delay-300">
              <div className="h-[2px] w-14 bg-primary-500" />
              <div className="w-2 h-2 bg-primary-500 rotate-45 -ml-[4px]" />
              <div className="h-[2px] flex-1 bg-primary-500/15 max-w-[100px]" />
            </div>

            {/* Opis */}
            <p className="text-[15px] xl:text-base text-foreground-600 leading-[1.75] mb-8 max-w-md animate-fade-in-up animation-delay-400">
              Jako doradca kredytowy pomagam dobrać kredyt hipoteczny, kredyt gotówkowy i kredyt firmowy do Twojej sytuacji finansowej. Współpracuję z wieloma bankami w Sanoku i na całym Podkarpaciu.
            </p>

            {/* Lokalizacja pill */}
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-accent-100/80 rounded-full mb-9 animate-fade-in-up animation-delay-500">
              <i className="ri-map-pin-line text-accent-500 text-sm" />
              <span className="text-xs font-semibold text-accent-700 tracking-wide">
                Sanok i całe Podkarpacie
              </span>
            </div>

            {/* CTA */}
            <div className="flex flex-col sm:flex-row gap-3 xl:gap-4 mb-10 animate-fade-in-up animation-delay-600">
              <a
                href="tel:698084044"
                className="relative overflow-hidden inline-flex px-8 py-3.5 bg-primary-500 text-white font-semibold rounded-full hover:bg-primary-600 hover:scale-105 active:scale-95 transition-all duration-300 shadow-xl shadow-primary-500/20 whitespace-nowrap cursor-pointer group"
              >
                <span className="relative z-10">Umów bezpłatną konsultację</span>
                <div className="absolute inset-0 bg-gradient-to-r from-primary-400 to-primary-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </a>
              <a
                href="tel:698084044"
                className="inline-flex px-8 py-3.5 border-2 border-primary-500 text-primary-500 font-semibold rounded-full hover:bg-primary-50 hover:scale-105 active:scale-95 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                Zadzwoń i poznaj ofertę
              </a>
            </div>

            {/* Trust signals */}
            <div className="flex flex-col gap-2.5 animate-fade-in-up animation-delay-700">
              {[
                "Bezpłatna pierwsza konsultacja",
                "Indywidualna analiza sytuacji",
                "Wsparcie na każdym etapie",
              ].map((text) => (
                <div key={text} className="flex items-center gap-2.5">
                  <div className="w-6 h-6 flex items-center justify-center rounded-full bg-primary-100">
                    <i className="ri-check-line text-primary-500 text-xs" />
                  </div>
                  <span className="text-sm font-medium text-foreground-600">{text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Prawa kolumna – zdjęcie z gradient blend */}
        <div className="w-[52%] xl:w-[54%] relative animate-fade-in animation-delay-300">
          <img
            src="https://storage.readdy-site.link/project_files/4e257fdf-86c3-45fa-9fad-66a82fd8796b/b811ef70-1d3c-4239-89f3-659617ce39fa_compressed_ChatGPT-Image-25-lip-2026-20_27_05.webp"
            alt="Jan Wojdyła – ekspert kredytowy Sanok"
            decoding="async"
            className="w-full h-full object-cover object-top"
          />
          {/* Gradient blend na styk z lewą kolumną */}
          <div className="absolute inset-y-0 left-0 w-20 xl:w-28 bg-gradient-to-r from-background-50 via-background-50/90 to-transparent" />
          <img
            src={commitmentBadge}
            srcSet={`${commitmentBadgeStandard} 360w, ${commitmentBadge} 720w`}
            sizes="(min-width: 1280px) 192px, (min-width: 1024px) 160px, 128px"
            alt="100% zaangażowania w Twoją sprawę"
            className="absolute bottom-7 left-7 z-10 w-[clamp(7.5rem,11vw,12rem)] drop-shadow-2xl xl:bottom-9 xl:left-10"
          />
          {/* Shimmer sweep */}
          <div className="absolute inset-0 pointer-events-none overflow-hidden" style={{ mixBlendMode: "overlay" }}>
            <div className="absolute inset-0 animate-shimmer bg-gradient-to-r from-transparent via-white/8 to-transparent -skew-x-12" />
          </div>
        </div>
      </div>

      {/* ============================================================
          MOBILE – Zdjęcie na cały ekran + jeden napis z animacją scale-in + subtelny parallax
      ============================================================ */}
      <div className="lg:hidden relative w-full h-screen max-h-[800px] min-h-[500px] overflow-hidden">

        {/* Warstwa parallax – przesuwa się przy scrollu */}
        <div
          className="absolute inset-0"
          style={{ transform: `translateY(${parallaxOffset}px)` }}
        >
          {/* Zdjęcie – placeholder widoczny do czasu załadowania wideo */}
          <img
            src="https://storage.readdy-site.link/project_files/4e257fdf-86c3-45fa-9fad-66a82fd8796b/bbe1874b-2eaf-4037-ac5b-4064e1a0fa27_compressed_mobilna.webp"
            alt="Jan Wojdyła – ekspert finansowy Sanok"
            decoding="async"
            className="absolute inset-0 w-full h-full object-cover object-top animate-hero-zoom"
          />

          {/* Wideo – ładuje się nad zdjęciem, płynnie się pojawia */}
          <video
            ref={videoRef}
            src="https://storage.readdy-site.link/project_files/4e257fdf-86c3-45fa-9fad-66a82fd8796b/db647fd7-9b3e-470c-a955-09b177d38280_janek-promo.mp4"
            autoPlay
            loop
            muted
            playsInline
            onPlaying={() => setVideoReady(true)}
            className={`absolute inset-0 w-full h-full object-cover object-top sm:object-[50%_25%] transition-opacity duration-700 ${
              videoReady ? 'opacity-100' : 'opacity-0'
            }`}
          />
        </div>

        {/* Ciemne tło pasujące do zdjęcia, gdzie obraz nie wypełnia */}
        <div className="absolute inset-0 bg-neutral-900 -z-10" />

        {/* Gradient od dołu dla czytelności tekstu */}
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950 via-foreground-950/50 to-transparent" />

        <img
          src={commitmentBadge}
          srcSet={`${commitmentBadgeStandard} 360w, ${commitmentBadge} 720w`}
          sizes="(min-width: 640px) 112px, 96px"
          alt="100% zaangażowania w Twoją sprawę"
          className="absolute right-4 top-4 z-10 w-24 drop-shadow-xl sm:right-6 sm:top-6 sm:w-28"
        />

        {/* JEDEN porządny napis na dole zdjęcia */}
        <div className="absolute bottom-0 left-0 right-0 flex flex-col items-center justify-end px-6 pb-10 sm:pb-12 pt-24 sm:pt-28 text-center">
          <div className="animate-hero-text-reveal">
            <img
              src={expertKredytowyLogo}
              srcSet={`${expertKredytowyLogoStandard} 640w, ${expertKredytowyLogo} 960w`}
              sizes="(min-width: 640px) 280px, 235px"
              alt="EXPERT KREDYTOWY"
              decoding="async"
              className="w-[235px] sm:w-[280px] h-auto mx-auto mb-5 drop-shadow-lg"
            />
            <h2 className={`font-heading text-[2.4rem] sm:text-[2.8rem] font-extrabold leading-[1] text-white mb-2 tracking-tight ${reducedMotion ? '' : 'animate-text-shimmer'}`}>
              ANALIZA FINANSOWANIA<br />360°
            </h2>
            <div className="flex items-center justify-center gap-2 mb-3">
              <div className="h-px w-6 bg-primary-400/60" />
              <span className="text-[12px] sm:text-[13px] font-semibold tracking-wide text-primary-300">
                Jan Wojdyła
              </span>
              <div className="h-px w-6 bg-primary-400/60" />
            </div>
            <p className="text-[14px] sm:text-[15px] text-white/70 leading-relaxed max-w-[260px] sm:max-w-xs mx-auto mb-6 font-body">
              Profesjonalna analiza dopasowana do Twojej sytuacji finansowej
            </p>
            <a
              href="tel:698084044"
              className="inline-flex items-center gap-2 px-7 py-3 sm:px-8 sm:py-3.5 bg-primary-500 text-white font-semibold rounded-full hover:bg-primary-400 hover:scale-105 active:scale-95 transition-all duration-300 shadow-xl shadow-primary-500/25 whitespace-nowrap cursor-pointer text-sm"
            >
              <i className="ri-phone-line text-base" />
              Zadzwoń i umów konsultację
            </a>
          </div>
        </div>


      </div>

      {/* Animacje */}
      <style>{`
        @keyframes shimmer {
          0% { transform: translateX(-150%) skewX(-12deg); }
          100% { transform: translateX(150%) skewX(-12deg); }
        }
        .animate-shimmer {
          animation: shimmer 5s ease-in-out infinite;
        }
        .animate-text-shimmer {
          background: linear-gradient(100deg, #fff 25%, rgba(255,255,255,.45) 45%, #fff 60%);
          background-size: 220% 100%;
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
          animation: textShimmer 4s ease-in-out infinite;
        }
        @keyframes textShimmer {
          0%, 100% { background-position: 100% 0; }
          50% { background-position: 0 0; }
        }

        @keyframes heroZoom {
          0% { transform: scale(1.08); }
          100% { transform: scale(1); }
        }
        .animate-hero-zoom {
          animation: heroZoom 1.8s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }

        @keyframes heroTextReveal {
          0% {
            opacity: 0;
            transform: scale(0.92) translateY(20px);
            filter: blur(8px);
          }
          60% {
            opacity: 1;
            filter: blur(0px);
          }
          100% {
            opacity: 1;
            transform: scale(1) translateY(0);
            filter: blur(0px);
          }
        }
        .animate-hero-text-reveal {
          opacity: 0;
          animation: heroTextReveal 1.4s cubic-bezier(0.16, 1, 0.3, 1) 0.3s forwards;
        }
      `}</style>
    </section>
  );
}
